import { Routes } from '@angular/router';
import {
  DEPOSIT_CRYPTO_METHODS,
  DEPOSIT_SKINS_METHODS,
  DEPOSIT_SURVEY_METHODS,
} from '@features/deposit/constants/deposit';
import { userGuard } from '@core/guards/user.guard';
import { adminGuard } from '@features/admin/guards/admin.guard';
import { adminEnvironmentGuard } from '@features/admin/guards/admin-environment.guard';
import { adminWithdrawGuard } from '@features/admin/guards/admin-witrhdraw.guard';

import { HomeComponent } from '@features/home/home.component';
import { AuthComponent } from '@features/auth/auth.component';

const games: Routes = [
  {
    path: 'cases',
    loadComponent: () =>
      import('./features/cases/cases-list/cases-list.component').then(
        (m) => m.CasesListComponent
      ),
  },
  {
    path: 'cases/:link',
    loadComponent: () =>
      import('./features/cases/cases-open/cases-open.component').then(
        (m) => m.CasesOpenComponent
      ),
  },
  {
    path: 'battles',
    loadComponent: () =>
      import('./features/case-battles/cb-list/cb-list.component').then(
        (m) => m.CbListComponent
      ),
  },
  {
    path: 'battles/create',
    loadComponent: () =>
      import('./features/case-battles/cb-create/cb-create.component').then(
        (m) => m.CbCreateComponent
      ),
  },
  {
    path: 'battles/:gameuid',
    loadComponent: () =>
      import('./features/case-battles/cb-battle/cb-battle.component').then(
        (m) => m.CbBattleComponent
      ),
  },
  {
    path: 'king',
    loadComponent: () =>
      import('./old/king/king/king.component').then((m) => m.KingComponent),
  },
  {
    path: 'king-new',
    loadComponent: () =>
      import('./features/king-new/king-new.component').then(
        (m) => m.KingNewComponent
      ),
  },
  {
    path: 'coinflip',
    loadComponent: () =>
      import('./old/coinflip/coinflip.component').then(
        (m) => m.CoinflipComponent
      ),
  },
  {
    path: 'coinflip/history',
    loadComponent: () =>
      import('./old/coinflip/coinflip-history/coinflip-history.component').then(
        (m) => m.CoinflipHistoryComponent
      ),
  },
  {
    path: 'plinko',
    loadComponent: () =>
      import('./old/plinko/plinko.component').then((m) => m.PlinkoComponent),
  },
  {
    path: 'jackpot',
    loadComponent: () =>
      import(
        './old/jackpot/jackpot-highroller/jackpot-highroller.component'
      ).then((m) => m.JackpotHighrollerComponent),
  },
  {
    path: 'upgrader',
    loadComponent: () =>
      import('./old/upgrader/upgrader/upgrader.component').then(
        (m) => m.UpgraderComponent
      ),
  },
  {
    path: 'upgrader-new',
    loadComponent: () =>
      import('./features/upgrader-new/upgrader-new.component').then(
        (m) => m.UpgraderNewComponent
      ),
  },
  {
    path: 'wheel',
    loadComponent: () =>
      import('./old/wheel/wheel/wheel.component').then((m) => m.WheelComponent),
  },
  {
    path: 'towers',
    loadComponent: () =>
      import('./old/towers/towers.component').then((m) => m.TowersComponent),
  },
  {
    path: 'mines',
    loadComponent: () =>
      import('./old/mines/mines.component').then((m) => m.MinesComponent),
  },
];

const admin: Routes = [
  {
    path: 'admin',
    loadComponent: () =>
      import('./features/admin/admin.component').then(
        (mod) => mod.AdminComponent
      ),
    canActivate: [adminGuard],
    children: [
      { path: '', redirectTo: 'users', pathMatch: 'full' },
      {
        path: 'new_dashboard',
        loadComponent: () =>
          import(
            './features/admin/admin-new-dashboard/admin-new-dashboard.component'
          ).then((mod) => mod.AdminNewDashboardComponent),
      },
      {
        path: 'old_dashboard',
        loadComponent: () =>
          import(
            './features/admin/admin-dashboard/admin-dashboard.component'
          ).then((mod) => mod.AdminDashboardComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/admin/admin-users/admin-users.component').then(
            (mod) => mod.AdminUsersComponent
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./features/admin/admin-support/admin-support.component').then(
            (mod) => mod.AdminSupportComponent
          ),
      },
      {
        path: 'items',
        loadComponent: () =>
          import('./features/admin/admin-items/admin-items.component').then(
            (mod) => mod.AdminItemsComponent
          ),
      },
      {
        path: 'unpriced',
        loadComponent: () =>
          import(
            './features/admin/admin-unpriced/admin-unpriced.component'
          ).then((mod) => mod.AdminUnpricedComponent),
      },
      {
        path: 'filter',
        loadComponent: () =>
          import('./features/admin/admin-filter/admin-filter.component').then(
            (mod) => mod.AdminFilterComponent
          ),
      },
      {
        path: 'limits',
        loadComponent: () =>
          import('./features/admin/admin-limits/admin-limits.component').then(
            (mod) => mod.AdminLimitsComponent
          ),
      },
      {
        path: 'games',
        loadComponent: () =>
          import('./features/admin/admin-games/admin-games.component').then(
            (mod) => mod.AdminGamesComponent
          ),
      },
      {
        path: 'trades',
        loadComponent: () =>
          import('./features/admin/admin-trades/admin-trades.component').then(
            (mod) => mod.AdminTradesComponent
          ),
      },
      {
        path: 'bots',
        loadComponent: () =>
          import('./features/admin/admin-bots/admin-bots.component').then(
            (mod) => mod.AdminBotsComponent
          ),
      },
      {
        path: 'case-creator',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-creator.component'
          ).then((mod) => mod.AdminCaseCreatorComponent),
      },
      {
        path: 'case-creator/case/:link',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-edit/admin-case-edit.component'
          ).then((mod) => mod.AdminCaseEditComponent),
      },
      {
        path: 'case-creator/new',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-edit/admin-case-edit.component'
          ).then((mod) => mod.AdminCaseEditComponent),
      },
      {
        path: 'withdraw',
        loadComponent: () =>
          import(
            './features/admin/admin-withdraw/admin-withdraw.component'
          ).then((mod) => mod.AdminWithdrawComponent),
        canActivate: [adminWithdrawGuard],
      },
      {
        path: 'environment',
        loadComponent: () =>
          import(
            './features/admin/admin-environment/admin-environment.component'
          ).then((mod) => mod.AdminEnvironmentComponent),
        canActivate: [adminEnvironmentGuard],
      },
      {
        path: 'new-pricing',
        loadComponent: () =>
          import(
            './features/admin/admin-new-pricing/admin-new-pricing.component'
          ).then((mod) => mod.AdminNewPricingComponent),
        loadChildren: () =>
          import(
            './features/admin/admin-new-pricing/admin-new-pricing.routes'
          ).then((mod) => mod.adminNewPricingRoutes),
      },
    ],
  },
];

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: HomeComponent,
  },
  {
    path: 'auth',
    component: AuthComponent,
  },
  // {
  //   path: 'faq',
  //   component: FaqComponent,
  // },
  // {
  //   path: 'tos',
  //   component: TosComponent,
  // },
  // {
  //   path: 'leaderboard',
  //   component: LeaderboardComponent,
  // },
  // {
  //   path: 'rewards',
  //   component: RewardsComponent,
  // },
  {
    path: 'leaderboard',
    loadComponent: () =>
      import('./features/leaderboard/leaderboard.component').then(
        (m) => m.LeaderboardComponent
      ),
  },
  {
    path: 'faq',
    loadComponent: () =>
      import('./features/faq/faq.component').then((m) => m.FaqComponent),
  },
  {
    path: 'tos',
    loadComponent: () =>
      import('./features/tos/tos.component').then((m) => m.TosComponent),
  },
  {
    path: 'rewards',
    loadComponent: () =>
      import('./features/rewards/rewards.component').then(
        (m) => m.RewardsComponent
      ),
  },
  {
    path: 'rewards/daily/:link',
    loadComponent: () =>
      import(
        './features/rewards/rewards-daily-unboxing/rewards-daily-unboxing.component'
      ).then((m) => m.RewardsDailyUnboxingComponent),
  },
  {
    path: 'rewards/reward/:link',
    loadComponent: () =>
      import(
        './features/rewards/rewards-reward-unboxing/rewards-reward-unboxing.component'
      ).then((m) => m.RewardsRewardUnboxingComponent),
  },
  {
    path: 'withdraw',
    // component: WithdrawComponent,
    loadComponent: () =>
      import('./features/withdraw/withdraw.component').then(
        (m) => m.WithdrawComponent
      ),
  },
  {
    path: 'provably-fair',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('./features/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('./features/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type/:id',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('./features/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type/:id/:roundid',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('./features/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'deposit',
    canActivate: [userGuard],
    // component: DepositComponent,
    loadComponent: () =>
      import('./features/deposit/deposit.component').then(
        (m) => m.DepositComponent
      ),
    children: [
      {
        path: '',
        // component: DepositMethodsListComponent,
        loadComponent: () =>
          import(
            './features/deposit/deposit-methods-list/deposit-methods-list.component'
          ).then((m) => m.DepositMethodsListComponent),
      },
      ...DEPOSIT_CRYPTO_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositCryptoComponent,
          loadComponent: () =>
            import(
              './features/deposit/deposit-crypto/deposit-crypto.component'
            ).then((m) => m.DepositCryptoComponent),
        };
      }),
      ...DEPOSIT_SURVEY_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositMethodsListComponent,
          loadComponent: () =>
            import(
              './features/deposit/deposit-methods-list/deposit-methods-list.component'
            ).then((m) => m.DepositMethodsListComponent),
        };
      }),
      ...DEPOSIT_SKINS_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositMethodsListComponent,
          loadComponent: () =>
            import(
              './features/deposit/deposit-methods-list/deposit-methods-list.component'
            ).then((m) => m.DepositMethodsListComponent),
        };
      }),
    ],
  },
  {
    path: 'support',
    canActivate: [userGuard],
    loadComponent: () =>
      import('./features/support/support.component').then(
        (m) => m.SupportComponent
      ),
  },
  {
    path: 'raffles',
    loadComponent: () =>
      import('./features/giveaways/giveaways.component').then(
        (m) => m.GiveawaysComponent
      ),
  },
  {
    path: 'affiliates',
    loadComponent: () =>
      import(
        '@features/affiliates/affiliates-new/affiliates-new.component'
      ).then((m) => m.AffiliatesNewComponent),
  },
  {
    path: 'affiliates/cases/:link',
    // component: AffiliatesUnboxingComponent,
    loadComponent: () =>
      import(
        '@features/affiliates/affiliates-unboxing/affiliates-unboxing.component'
      ).then((m) => m.AffiliatesUnboxingComponent),
  },
  {
    path: 'r/:affCode',
    loadComponent: () =>
      import(
        '@features/affiliates/affiliates-new/affiliates-new.component'
      ).then((m) => m.AffiliatesNewComponent),
  },
  {
    path: 'p/:promoCode',
    loadComponent: () =>
      import('./features/home/home.component').then((m) => m.HomeComponent),
  },
  ...games,
  ...admin,
  {
    path: '**',
    redirectTo: 'jackpot',
  },
];
